/**
 * A person's Restore from the Bin, end to end (#854): request, progress toast,
 * outcome.
 *
 * Small entries answer inline, as before. A big one is queued as a background
 * job (202) and followed over the websocket (`createRestoreTracker`); the toast
 * turns into a real bar counting restored rows. The row's "Restoring…" state
 * comes from the server's marker (`BinItem.restoring`), so every window shows
 * it, not only this one; `inFlightRestores` covers this tab while its request
 * is out.
 *
 * A job still running when the page reloads is picked up again: its id is kept
 * in sessionStorage and followed on the next mount.
 *
 * The request's own refusals (the original folder is gone, an access change to
 * confirm, no Manage) are THROWN to the caller, which owns those dialogs.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useRealtime } from '@papan/realtime/client'
import { useToast } from '@papan/core-ui/ui'
import { sessionSignal } from '@papan/core-ui/ui/sessionEnded'
import { useTranslation } from '@papan/i18n'
import { BIN_ITEM_BEING_RESTORED_CODE, FOLDER_TOO_LARGE_TO_RESTORE_CODE, TENANT_WRITE_FROZEN_CODE, RESERVED_ROOT_CODE } from '@papan/types'
import { reservedRootRestoreText } from './restore-refusal'
import {
  filesApi,
  createRestoreTracker,
  pendingRestores,
  inFlightRestores,
  requestOutcomeUnknown,
  JobFailedError,
  JobLostError,
  type RestoreJobOutcome,
  type RestoredInto,
  type RestoreJobProgress,
  type PendingRestore,
  type RestoreTracker,
  type RestoreStartResult,
  type RestoreRequestOptions,
} from '@papan/files/client'

export interface RestoreTarget {
  binId: string
  name: string
}

export interface RestoreRunOptions {
  /** A restore finished (inline or as a job, including one resumed after a reload). */
  onRestored?: (target: RestoreTarget, outcome: RestoreJobOutcome) => void
}

/**
 * How a Restore ended, for the caller's own toast. `done`: it answered inline
 * (the caller says "restored"). `queued`: it ran as a job and this runner's
 * progress toast already said how it ended.
 */
export type RestoreRun =
  | { kind: 'done'; fileId: string; recreatedFolders?: string[]; renamedTo?: string; restoredInto?: RestoredInto }
  | { kind: 'queued'; ok: boolean }
  /** E7 PR 0b: the session ended (sign-out, a tenant / identity switch) while the request was out — its answer, if
   *  any, is dropped and nothing is said. */
  | { kind: 'cancelled' }

/**
 * A refusal the runner's callers show as a plain toast, in the person's
 * language; null for anything else (so the caller falls back to its own text).
 */
export function restoreFailureText(err: unknown, name: string, t: (key: string, o?: Record<string, unknown>) => string): string | null {
  const code = (err as { code?: string } | null)?.code
  if (code === BIN_ITEM_BEING_RESTORED_CODE) return t('binView.toast.beingRestored', { name })
  if (code === FOLDER_TOO_LARGE_TO_RESTORE_CODE) return t('binView.toast.tooLargeToRestore', { name })
  // D9: uploads paused refuses a restore — said as a restore that waits on payment,
  // not as "uploads", which the person did not try to do.
  if (code === TENANT_WRITE_FROZEN_CODE) return t('binView.toast.restorePaused', { name })
  // #1119: a folder that would land as a reserved top-level folder. This toast opens no picker (a Bin that offers one
  // answers before reaching here), so it says who can put it elsewhere (review round 5).
  if (code === RESERVED_ROOT_CODE) return reservedRootRestoreText(t, err, name, false)
  if (err instanceof JobLostError) return t('binView.toast.restoreOutcomeUnknown', { name })
  // A job's own failure carries the server's plain-words reason.
  if (err instanceof JobFailedError && err.message) return err.message
  return null
}

/** Jobs this tab is already following — a second click (which joins the same
 *  job) or a second mounted view must not open a second toast. */
const following = new Set<string>()

function newOperationId(): string {
  return `restore-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function useRestoreRunner(options: RestoreRunOptions = {}) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const { status, onOperationEvent } = useRealtime()
  const statusRef = useRef(status)
  statusRef.current = status
  const optsRef = useRef(options)
  optsRef.current = options

  const deps = useCallback(() => ({
    onOperationEvent,
    isConnected: () => statusRef.current === 'connected',
    getStatus: (jobId: string) => filesApi.getRestoreStatus(jobId),
  }), [onOperationEvent])

  const failText = useCallback((err: unknown, name: string): string =>
    restoreFailureText(err, name, t) ?? t('binView.toast.restoreFailed'), [t])

  const progressText = useCallback((p: RestoreJobProgress, name: string): string =>
    p.total > 0
      ? t('binView.toast.restoringCount', { name, done: p.done.toLocaleString(), total: p.total.toLocaleString() })
      : t('binView.toast.restoring', { name }), [t])

  /** Follow one queued job to its end with a progress toast; true when it restored. E7 PR 0b: when the session ends
   *  (`signal`, the session's — `sessionSignal`) the bar goes away and nothing more is said — the job is the server's, its outcome
   *  belongs to the account that started it. */
  const followWithToast = useCallback(async (pending: PendingRestore, tracker: RestoreTracker, signal: AbortSignal = sessionSignal()): Promise<boolean> => {
    following.add(pending.jobId)
    pendingRestores.add(pending)
    inFlightRestores.mark([pending.binId], true)
    const progress = toast.progress(t('binView.toast.restoring', { name: pending.name }), 0)
    let onEnd: (() => void) | undefined
    const ended = new Promise<never>((_res, reject) => {
      onEnd = () => reject(signal.reason)
      if (signal.aborted) onEnd(); else signal.addEventListener('abort', onEnd, { once: true })
    })
    ended.catch(() => {})
    try {
      const result = await Promise.race([tracker.follow(pending.jobId, pending.operationId, (p) => {
        if (!signal.aborted) progress.update(p.total > 0 ? p.done : 0, progressText(p, pending.name), p.total > 0 ? p.total : 0)
      }), ended])
      // #826 R4: the job's events go out tenant-wide and never carry a name, so the name it
      // came back under (another item may hold the old one) is read as the person, by id — only
      // when the job says it was renamed: a merge into its live folder keeps a manager's rename,
      // which is no clash (an older worker sends no flag: read it back as before).
      const finalName = result.renamed === false
        ? pending.name
        : await filesApi.getFileInfo(result.fileId).then(f => f.name, () => pending.name)
      if (signal.aborted) { toast.removeToast(progress.id); return false }
      progress.complete(finalName !== pending.name
        ? t('binView.toast.restoredAs', { name: pending.name, newName: finalName })
        : t('binView.toast.restoredOne', { name: pending.name }))
      optsRef.current.onRestored?.({ binId: pending.binId, name: pending.name }, result)
      return true
    } catch (err) {
      if (signal.aborted) { tracker.dispose(); toast.removeToast(progress.id); return false }
      progress.fail(failText(err, pending.name))
      return false
    } finally {
      if (onEnd) signal.removeEventListener('abort', onEnd)
      following.delete(pending.jobId)
      pendingRestores.remove(pending.jobId)
      inFlightRestores.mark([pending.binId], false)
    }
  }, [toast, t, progressText, failText])

  /**
   * Restore one Bin entry. Resolves when it has finished (inline, or its job's
   * end). Throws the request's refusals, as `filesApi.restoreFromBin` does.
   */
  const restore = useCallback(async (
    target: RestoreTarget,
    targetParentId?: string | null,
    opts?: RestoreRequestOptions,
  ): Promise<RestoreRun> => {
    const operationId = newOperationId()
    const tracker = createRestoreTracker(deps(), operationId)
    // E7 PR 0b: the request belongs to the SESSION (`sessionSignal`), never the view — leaving the Bin doesn't abandon
    // it (its outcome is told through the app-wide toasts wherever the person is); the session ending aborts it in
    // flight, and an answer landing after that is dropped. No automatic retry: a "still verifying" 503 is a refusal.
    const signal = sessionSignal()
    const cancelled = (): RestoreRun => ({ kind: 'cancelled' })
    const send = () => filesApi.restoreFromBinAsync(target.binId, operationId, targetParentId, { ...opts, signal })
    let started: RestoreStartResult
    inFlightRestores.mark([target.binId], true)
    try {
      try {
        started = await send()
      } catch (err) {
        if (signal.aborted) throw err
        if (!requestOutcomeUnknown(err)) throw err
        // No answer arrived, so the server may have acted anyway. Asking again
        // is safe: the same person's second Restore joins the running job; an
        // inline restore that already finished answers 404.
        try {
          started = await send()
        } catch (retryErr) {
          if (requestOutcomeUnknown(retryErr) || (retryErr as { status?: number })?.status === 404) throw new JobLostError()
          throw retryErr
        }
      }
    } catch (err) {
      tracker.dispose()
      inFlightRestores.mark([target.binId], false)
      if (signal.aborted) return cancelled()
      throw err
    }
    // an answer that arrived as the session ended is not told (nor handed to the view) either
    if (signal.aborted) { tracker.dispose(); inFlightRestores.mark([target.binId], false); return cancelled() }
    if (started.kind === 'done') {
      tracker.dispose()
      inFlightRestores.mark([target.binId], false)
      optsRef.current.onRestored?.(target, { fileId: started.fileId })
      return {
        kind: 'done', fileId: started.fileId,
        ...(started.recreatedFolders ? { recreatedFolders: started.recreatedFolders } : {}),
        ...(started.renamedTo ? { renamedTo: started.renamedTo } : {}),
        ...(started.restoredInto ? { restoredInto: started.restoredInto } : {}),
      }
    }
    if (following.has(started.jobId)) {
      // A second click joined the job this tab already follows: its toast speaks.
      tracker.dispose()
      inFlightRestores.mark([target.binId], false)
      return { kind: 'queued', ok: true }
    }
    const pending: PendingRestore = {
      jobId: started.jobId, operationId: started.operationId, binId: target.binId, name: target.name, startedAt: Date.now(),
    }
    // The job's own mark goes on (synchronously, first thing) before the
    // request's comes off, so the row never flickers back to idle.
    const finished = followWithToast(pending, tracker, signal)
    inFlightRestores.mark([target.binId], false)
    const ok = await finished
    return signal.aborted ? cancelled() : { kind: 'queued', ok }
  }, [deps, followWithToast, toast, t])

  // ── Resume jobs a reload interrupted ──────────────────────────────────
  useEffect(() => {
    for (const p of pendingRestores.list().filter(x => !following.has(x.jobId))) {
      // A distinct own-id: the tracker then asks the status route once at the
      // start, since the outcome may have gone out while the page was away.
      void followWithToast(p, createRestoreTracker(deps(), `resume-${p.jobId}`))
    }
    // Once per mount: a reload is the only way to lose a job this tab started.
  }, [])

  return { restore }
}
